import React, { useState, useRef, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  getProjectsWithCalls,
} from "../../../services/grantsApi";
import OperatorProjectTree from "./OperatorProjectTree";

/**
 * Sentinel value returned by getErrorMessage when operator is not found (404).
 */
const ERROR_NOT_FOUND = "not_found" as const;

/**
 * Props for OperatorOtherProjectsSection component
 * @see docs/specifications/2026-03-16-operator-other-projects-in-recommendation.md
 */
interface OperatorOtherProjectsSectionProps {
  operatorId: string;
  currentProjectId?: string;
}

/**
 * Re-export truncateTitle from OperatorProjectTree for backward compatibility.
 */
export { truncateTitle } from "./OperatorProjectTree";

/**
 * Chevron icon for expandable section
 */
const ChevronIcon: React.FC<{ expanded: boolean }> = ({ expanded }) => (
  <svg
    className={`h-5 w-5 transition-transform duration-200 ${expanded ? "rotate-180" : ""}`}
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    aria-hidden="true"
  >
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
  </svg>
);

/**
 * Skeleton loader for projects table (2 rows)
 */
const ProjectsSkeleton: React.FC = () => (
  <div className="space-y-2" role="status" aria-label="Ładowanie projektów">
    {[1, 2].map((i) => (
      <div key={i} className="animate-pulse flex items-center gap-4 p-2 border border-neutral-200 rounded-md">
        <div className="flex-1 space-y-2">
          <div className="h-4 bg-neutral-200 rounded w-2/3"></div>
          <div className="h-3 bg-neutral-200 rounded w-1/3"></div>
        </div>
        <div className="h-6 bg-neutral-200 rounded w-16"></div>
      </div>
    ))}
  </div>
);

/**
 * Error alert with retry button (compact version)
 */
const ErrorAlert: React.FC<{ message: string; onRetry: () => void }> = ({ message, onRetry }) => (
  <div className="p-3 bg-red-50 border border-red-200 rounded-md text-sm">
    <div className="flex items-center justify-between">
      <span className="text-red-700">{message}</span>
      <button
        onClick={onRetry}
        aria-label="Ponów pobieranie projektów operatora"
        className="px-2 py-1 text-xs font-medium text-red-700 bg-white border border-red-300 rounded hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500"
      >
        Odśwież
      </button>
    </div>
  </div>
);

/**
 * Collapsible section displaying other projects of the same operator.
 * Lazy loads data only when expanded.
 * Always visible when operator is known (even if empty).
 *
 * @see docs/specifications/2026-03-16-operator-other-projects-in-recommendation.md
 */
const OperatorOtherProjectsSection: React.FC<OperatorOtherProjectsSectionProps> = ({
  operatorId,
  currentProjectId,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);

  // Lazy loading - only fetch when expanded
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["operator-projects-with-calls", operatorId, currentProjectId],
    queryFn: () => getProjectsWithCalls(operatorId, currentProjectId),
    enabled: !!operatorId && isExpanded,
    staleTime: 60000,
  });

  const projects = data?.projects || [];
  const totalProjectCount = data?.totalProjectCount ?? 0;

  // Determine if data is truncated (server limits to 10)
  const hasMoreProjects = totalProjectCount > projects.length;

  // Focus management after toggle
  const pendingFocusRef = useRef<"content" | "button" | null>(null);

  const handleToggle = () => {
    setIsExpanded((prev) => {
      pendingFocusRef.current = !prev ? "content" : "button";
      return !prev;
    });
  };

  useEffect(() => {
    if (pendingFocusRef.current === "content" && contentRef.current) {
      contentRef.current.focus();
      pendingFocusRef.current = null;
    } else if (pendingFocusRef.current === "button" && toggleRef.current) {
      toggleRef.current.focus();
      pendingFocusRef.current = null;
    }
  }, [isExpanded]);

  // Error message mapping
  const getErrorMessage = (err: unknown): string | null => {
    const errorObj = err as { response?: { status?: number } };

    // 403 - hide section
    if (errorObj?.response?.status === 403) {
      return null;
    }

    // 404 - operator not found
    if (errorObj?.response?.status === 404) {
      return ERROR_NOT_FOUND;
    }

    if (err instanceof Error) {
      if (err.name === "TimeoutError" || err.message.includes("timeout")) {
        return "Przekroczono czas oczekiwania";
      }
      if (err.message.includes("network") || err.message.includes("Network")) {
        return "Błąd połączenia";
      }
    }
    return "Nie udało się pobrać projektów operatora";
  };

  // Hide section if 403 error
  const errorMessage = error ? getErrorMessage(error) : null;
  if (error && errorMessage === null) {
    return null;
  }

  return (
    <section
      aria-labelledby="operator-other-projects-heading"
      className="bg-white rounded-lg shadow-sm border border-neutral-200"
    >
      <div className="px-6 py-4">
        <h2 id="operator-other-projects-heading" className="contents">
          <button
            ref={toggleRef}
            onClick={handleToggle}
            className="flex items-center justify-between w-full text-left"
            aria-expanded={isExpanded}
            aria-controls="operator-other-projects-content"
            id="operator-other-projects-toggle"
          >
            <span className="text-lg font-medium text-neutral-900">
              Inne projekty operatora
            </span>
            <ChevronIcon expanded={isExpanded} />
          </button>
        </h2>

        <div
          ref={contentRef}
          id="operator-other-projects-content"
          aria-labelledby="operator-other-projects-toggle"
          aria-live="polite"
          tabIndex={-1}
          className={`mt-4 pt-4 border-t border-neutral-200 ${isExpanded ? "" : "hidden"}`}
        >
          {isLoading && <ProjectsSkeleton />}

          {error && !isLoading && errorMessage === ERROR_NOT_FOUND && (
            <p className="text-center text-sm text-neutral-500 py-2">
              Brak danych
            </p>
          )}

          {error && !isLoading && errorMessage !== null && errorMessage !== ERROR_NOT_FOUND && (
            <ErrorAlert message={errorMessage} onRetry={() => refetch()} />
          )}

          {!isLoading && !error && projects.length === 0 && (
            <p className="text-center text-sm text-neutral-500 py-2">
              Brak innych projektów
            </p>
          )}

          {!isLoading && !error && projects.length > 0 && (
            <>
              <OperatorProjectTree projects={projects} />
              {hasMoreProjects && (
                <p className="mt-2 text-xs text-neutral-500 text-center">
                  Pokazano {projects.length} z {totalProjectCount} projektów
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </section>
  );
};

export default OperatorOtherProjectsSection;
